import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CmpIdaAnswerSourceEnum,
  useAskCmpIdaMutation,
  useGetCmpIdaChatQuery,
} from "../../../graphql/types";
import { guideHref } from "../../pages/UserGuide/CmpUserGuide";
import { getUserFacingErrorText } from "../../../utils/userFacingError";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../utils/apolloCallerContext";

// IDA chat bot (Monday 13270244629, Issue Fixer 10), CMP Dashboard (20)
// CMP Dashboard.dc.html:184-235 and CLAUDE.md "IDA chat bot": greeting bubble
// above a black launcher, fixed bottom-right; the chat lists the common
// questions under the greeting; closing the chat resets it; the X hides the
// greeting for this visit only; hidden when printing. The backend decides where
// IDA shows (Sand Bay only for now) and answers every question.

const FONT = "'Plus Jakarta Sans','DM Sans',sans-serif";
const IDA_IMAGE = "/assets/ida-gold.png";
const HISTORY_SENT = 6;
// Design 520px; Moeen (Oct 18 2026) asked for IDA about 33% taller.
export const IDA_PANEL_HEIGHT = 692;

type IdaMessage = {
  fromIda: boolean;
  text: string;
  guidePages?: { slug: string; title: string }[];
};

const IdaAvatar = ({ size, image }: { size: number; image: number }) => (
  <div
    style={{
      flexShrink: 0, width: size, height: size, borderRadius: "50%", background: "#fff",
      border: "1px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "center",
    }}
  >
    <img src={IDA_IMAGE} alt="IDA" style={{ width: image, height: "auto", display: "block" }} />
  </div>
);

// Answers are plain text with a simple layout (backend rule): topics are
// separated by a blank line, steps are "1. " lines and lists are "- " lines.
// Each topic gets its own spaced block so an answer never reads as one paragraph.
export const IdaAnswerText = ({ text }: { text: string }) => {
  const blocks = text.split(/\n\s*\n/).map((block) => block.split("\n").filter((line) => line.trim())).filter((block) => block.length);
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {blocks.map((lines, blockIndex) => (
        <span key={blockIndex} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {lines.map((line, lineIndex) => {
            const step = /^(\d+)\.\s+(.*)$/.exec(line.trim());
            const bullet = /^-\s+(.*)$/.exec(line.trim());
            if (step || bullet) {
              return (
                <span key={lineIndex} style={{ display: "flex", gap: 6 }}>
                  <span style={{ flexShrink: 0, minWidth: step ? 16 : 10, fontWeight: step ? 600 : 400 }}>
                    {step ? `${step[1]}.` : "•"}
                  </span>
                  <span>{step ? step[2] : bullet![1]}</span>
                </span>
              );
            }
            return <span key={lineIndex}>{line}</span>;
          })}
        </span>
      ))}
    </span>
  );
};

export const CmpIdaChat = ({ onEnabledChange }: { onEnabledChange?: (enabled: boolean) => void }) => {
  const navigate = useNavigate();
  const { data } = useGetCmpIdaChatQuery({ fetchPolicy: "cache-first", context: CALLER_HANDLED_USER_ERROR_CONTEXT });
  const [askIda, { loading: asking }] = useAskCmpIdaMutation({ context: CALLER_HANDLED_USER_ERROR_CONTEXT });
  const [greetingClosed, setGreetingClosed] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<IdaMessage[]>([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const chat = data?.getCmpIdaChat;
  const enabled = !!chat?.enabled;

  useEffect(() => {
    onEnabledChange?.(enabled);
  }, [enabled, onEnabledChange]);

  // Design (21) CMP Dashboard.dc.html:17288: when IDA answers, the start of
  // the answer sits at the top of the chat (8px gap), never its end. A new
  // question or the writing bubble scrolls to the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!asking && messages[messages.length - 1]?.fromIda) {
      const answers = el.querySelectorAll<HTMLElement>("[data-ida-msg]");
      const answer = answers[answers.length - 1];
      if (answer) {
        el.scrollTop += answer.getBoundingClientRect().top - el.getBoundingClientRect().top - 8;
        return;
      }
    }
    el.scrollTop = el.scrollHeight;
  }, [messages, asking]);

  if (!enabled || !chat) return null;

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || asking) return;
    const history = messages.slice(-HISTORY_SENT).map((message) => ({ fromIda: message.fromIda, text: message.text }));
    setMessages((current) => [...current, { fromIda: false, text }]);
    try {
      const result = await askIda({ variables: { input: { question: text, history } } });
      const answer = result.data?.askCmpIda;
      setMessages((current) => [
        ...current,
        answer
          ? {
              fromIda: true,
              text: answer.answer,
              guidePages: answer.source === CmpIdaAnswerSourceEnum.Ai ? answer.guidePages : [],
            }
          : { fromIda: true, text: "IDA could not answer right now. Try again, or click Need Help? in the sidebar to create a support ticket." },
      ]);
    } catch (error: unknown) {
      setMessages((current) => [
        ...current,
        {
          fromIda: true,
          text: getUserFacingErrorText(
            error,
            "IDA could not answer right now. Try again, or click Need Help? in the sidebar to create a support ticket."
          ),
        },
      ]);
    }
  };

  const closeChat = () => {
    setChatOpen(false);
    setMessages([]);
    setInput("");
  };

  const firstMessage = [chat.firstMessage, chat.sampleLines.map((line) => `- ${line}`).join("\n")].join("\n\n");

  return (
    <div
      data-ida-chat
      className="print:hidden"
      style={{
        position: "fixed", right: 24, bottom: 24, zIndex: 15, fontFamily: FONT,
        display: "flex", flexDirection: "column", alignItems: "flex-end",
      }}
    >
      {!greetingClosed && !chatOpen ? (
        <div style={{ position: "relative", width: 210, paddingTop: 21, marginBottom: 12, marginRight: 4 }}>
          <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", zIndex: 1 }}>
            <IdaAvatar size={42} image={30} />
          </div>
          <button
            type="button"
            onClick={() => setChatOpen(true)}
            className="hover:shadow-[0_10px_28px_rgba(0,0,0,.16)]"
            style={{
              width: "100%", background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16,
              boxShadow: "0 8px 24px rgba(0,0,0,.10)", padding: "28px 16px 14px", display: "flex",
              flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center", cursor: "pointer", fontFamily: FONT,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: "#0F1B3D" }}>👋Hi, I’m IDA!</span>
            <span style={{ fontSize: 13, fontWeight: 400, color: "#0F1B3D" }}>How can I help you?</span>
          </button>
          <button
            type="button"
            onClick={() => setGreetingClosed(true)}
            aria-label="Close"
            className="text-[#727272] hover:text-[#0A0A0A]"
            style={{
              position: "absolute", top: 30, right: 10, zIndex: 2, width: 22, height: 22, display: "flex",
              alignItems: "center", justifyContent: "center", border: "none", background: "transparent", cursor: "pointer", padding: 0,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
      ) : null}

      {chatOpen ? (
        <div
          role="region"
          aria-label="IDA chat"
          style={{
            width: 360, maxWidth: "calc(100vw - 48px)", height: IDA_PANEL_HEIGHT, maxHeight: "calc(100vh - 48px)", background: "#fff",
            border: "1px solid #ECECEC", borderRadius: 16, boxShadow: "0 14px 40px rgba(0,0,0,.18)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#0A0A0A", color: "#fff" }}>
            <IdaAvatar size={38} image={27} />
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>IDA</span>
              <span style={{ fontSize: 12, color: "#BDBDBD" }}>Intelligent Digital Assistant</span>
            </div>
            <button
              type="button"
              onClick={closeChat}
              aria-label="Minimize"
              style={{
                width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                border: "none", background: "transparent", color: "#fff", cursor: "pointer", padding: 0,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          <div
            ref={scrollRef}
            aria-live="polite"
            style={{
              flex: 1, minHeight: 0, overflowY: "auto", padding: 14, display: "flex",
              flexDirection: "column", gap: 10, background: "#F7F7F8",
            }}
          >
            {[{ fromIda: true, text: firstMessage } as IdaMessage, ...messages].map((message, index) => (
              <React.Fragment key={index}>
                {message.fromIda ? (
                  <div data-ida-msg style={{ display: "flex", justifyContent: "flex-start", gap: 8, alignItems: "flex-end" }}>
                    <IdaAvatar size={26} image={18} />
                    <div
                      style={{
                        maxWidth: "82%", background: "#fff", color: "#0A0A0A", border: "1px solid #ECECEC",
                        borderRadius: "14px 14px 14px 4px", padding: "9px 12px", fontSize: 13, lineHeight: 1.5,
                        // CMP Dashboard.dc.html:213 IDA bubble (A26).
                        textWrap: "pretty",
                      }}
                      data-testid="ida-answer-bubble"
                    >
                      <IdaAnswerText text={message.text} />
                      {message.guidePages?.length ? (
                        <span style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 10 }}>
                          {message.guidePages.map((page) => (
                            <button
                              key={page.slug}
                              type="button"
                              onClick={() => navigate(guideHref(page.slug))}
                              style={{
                                textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer",
                                color: "#0A0A0A", textDecoration: "underline", fontSize: 13, fontFamily: FONT,
                              }}
                            >
                              Open in User Guide: {page.title}
                            </button>
                          ))}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div
                      style={{
                        maxWidth: "80%", background: "#0A0A0A", color: "#fff", borderRadius: "14px 14px 4px 14px",
                        padding: "9px 12px", fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap",
                      }}
                    >
                      {message.text}
                    </div>
                  </div>
                )}
                {index === 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingLeft: 34 }}>
                    <span style={{ fontSize: 12, color: "#727272" }}>Common questions</span>
                    {chat.commonQuestions.map((question) => (
                      <button
                        key={question}
                        type="button"
                        onClick={() => ask(question)}
                        disabled={asking}
                        className="hover:bg-[#FFF7E6]"
                        style={{
                          textAlign: "left", background: "#fff", border: "1px solid #E1A731", borderRadius: 10,
                          padding: "8px 12px", fontSize: 13, color: "#0A0A0A", cursor: "pointer", fontFamily: FONT,
                        }}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                ) : null}
              </React.Fragment>
            ))}
            {asking ? (
              <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, alignItems: "flex-end" }}>
                <IdaAvatar size={26} image={18} />
                <div
                  style={{
                    background: "#fff", color: "#727272", border: "1px solid #ECECEC", borderRadius: "14px 14px 14px 4px",
                    padding: "9px 12px", fontSize: 13, lineHeight: 1.5,
                  }}
                >
                  IDA is writing an answer…
                </div>
              </div>
            ) : null}
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              const value = input.trim();
              if (!value || asking) return;
              setInput("");
              ask(value);
            }}
            style={{ display: "flex", gap: 8, padding: 10, borderTop: "1px solid #ECECEC", background: "#fff" }}
          >
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Type your question…"
              aria-label="Type your question"
              maxLength={500}
              style={{
                flex: 1, minWidth: 0, border: "1px solid #D4D4D4", borderRadius: 8, padding: "10px 12px",
                fontSize: 14, outline: "none", fontFamily: FONT,
              }}
            />
            <button
              type="submit"
              disabled={asking}
              className="bg-[#E1A731] hover:bg-[#C6912A]"
              style={{
                border: "none", borderRadius: 8, color: "#fff", padding: "0 14px", fontSize: 14,
                fontWeight: 600, cursor: "pointer", fontFamily: FONT,
              }}
            >
              Send
            </button>
          </form>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setChatOpen(true)}
          aria-label="Chat with IDA"
          className="bg-[#0A0A0A] hover:bg-[#2A2A2A]"
          style={{
            width: 56, height: 56, borderRadius: "50%", border: "none", boxShadow: "0 6px 18px rgba(0,0,0,.18)",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, color: "#fff",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default CmpIdaChat;
